import React, { useMemo, useState } from 'react';
import {
    Rule,
    RoleInfo,
    VocabItem,
    Vocabulary,
    saveRole,
} from '../../api/authz';
import { ApiError } from '../../api/request';

/**
 * 角色编辑器 (2026-09-11 三档角色: 动作 get/create/update/delete/publish/approve).
 *
 * 主界面按资源域给三档 —— 无 / 只读(get) / 管理员(该资源全部动作, **不含审批**), 对应
 * "普通用户 / 资源管理员" 两档; 审批只有系统管理员 (通配 *:*) 有, 编辑器不展示、也不可配 —— 但已有的 approve 在保存时原样保留。
 * 两种模式共用同一份勾选态, 档位只是矩阵的预设组合, 不是另一套数据。
 *
 * 审批口径: 资源的写动作 = 发起变更 (走审批); 资源的 approve = 批准/驳回该资源的工单;
 * 看自己提交的工单不需要任何审批权限。
 */

const READ_VERBS = ['get'];
/** 档位模式与矩阵都不展示的动作: 审批只归系统管理员, 不在编辑器里配 (已有的保存时保留). */
const LEVEL_EXCLUDED_VERBS = ['approve'];

type Level = 'none' | 'read' | 'edit';

const LEVEL_META: Record<Level, { label: string; hint: string }> = {
    none: { label: '无', hint: '完全看不到该资源' },
    read: { label: '只读', hint: '查看列表与详情、导出' },
    edit: {
        label: '管理员',
        hint: '该资源的全部动作: 查看/新建/编辑/删除/发布 (写操作提交后走审批生效); 不含审批',
    },
};

/**
 * 中间档按资源覆写 (2026-09-14): 有些资源"只读"不是有意义的中间档.
 * OpenAPI 令牌的中间档是「自管」= 看 / 签 / 吊销 / 删除自己的令牌 (get/create/delete),
 * 「管理员」才多出 update (看全站令牌、调限流). 普通用户默认就是自管档.
 */
const MID_LEVEL_OVERRIDE: Record<
    string,
    { verbs: string[]; label: string; hint: string }
> = {
    api_token: {
        verbs: ['get', 'create', 'delete'],
        label: '自管',
        hint: '查看 / 签发 / 吊销 / 删除自己的令牌 (普通用户默认档); 看全站令牌与限流要管理员',
    },
};

/** 某资源中间档的显示名与说明 (默认只读, 有覆写的按覆写). */
function midLevelMeta(r: VocabItem) {
    return MID_LEVEL_OVERRIDE[r.key] || LEVEL_META.read;
}

const cellKey = (resource: string, verb: string) => `${resource}:${verb}`;

/** 该资源实际可用的动作 (后端按登记表推导); 缺省回退到全部动作. */
function usableVerbs(r: VocabItem, all: VocabItem[]): string[] {
    return r.verbs && r.verbs.length > 0 ? r.verbs : all.map((v) => v.key);
}

/** 档位 → 具体动作: 与该资源实际可用的动作取交集, 不授不存在的权限. */
function verbsForLevel(level: Level, r: VocabItem, all: VocabItem[]): string[] {
    const usable = usableVerbs(r, all);
    if (level === 'read') {
        const mid = MID_LEVEL_OVERRIDE[r.key];
        return usable.filter((v) =>
            mid ? mid.verbs.includes(v) : READ_VERBS.includes(v),
        );
    }
    if (level === 'edit')
        return usable.filter((v) => !LEVEL_EXCLUDED_VERBS.includes(v)); // 管理员档: 全部动作减审批
    return [];
}

/** 某资源可选的档位: 只列有区别的 (纯只读型资源不列"编辑"). */
function levelsFor(r: VocabItem, all: VocabItem[]): Level[] {
    const usable = usableVerbs(r, all);
    const out: Level[] = ['none'];
    if (usable.some((v) => READ_VERBS.includes(v))) out.push('read');
    if (
        usable.some(
            (v) => !READ_VERBS.includes(v) && !LEVEL_EXCLUDED_VERBS.includes(v),
        )
    )
        out.push('edit');
    return out;
}

/**
 * 当前档位; 与任何档位都对不上则为 null (自定义组合, 去精确模式看).
 * 审批 (approve) 不参与档位判定 —— 它是档位之外的独立开关 (只在精确矩阵里勾),
 * 否则 admin 这种"全部动作含审批"的角色每行都会落到"自定义组合" (2026-09-13).
 */
function levelOf(
    cells: Set<string>,
    r: VocabItem,
    all: VocabItem[],
): Level | null {
    const on = usableVerbs(r, all).filter(
        (v) =>
            cells.has(cellKey(r.key, v)) && !LEVEL_EXCLUDED_VERBS.includes(v),
    );
    if (on.length === 0) return 'none';
    const same = (a: string[], b: string[]) =>
        a.length === b.length && a.every((x) => b.includes(x));
    for (const lvl of ['read', 'edit'] as Level[]) {
        if (same(on, verbsForLevel(lvl, r, all))) return lvl;
    }
    return null;
}

/** 规则 → 勾选态; 通配 "*" 展开成词表内的具体值. */
function rulesToCells(
    rules: Rule[],
    resources: VocabItem[],
    verbs: VocabItem[],
): Set<string> {
    const cells = new Set<string>();
    rules.forEach((rule) => {
        const rs = rule.resources.includes('*')
            ? resources.map((r) => r.key)
            : rule.resources;
        const vs = rule.verbs.includes('*')
            ? verbs.map((v) => v.key)
            : rule.verbs;
        rs.forEach((r) => vs.forEach((v) => cells.add(cellKey(r, v))));
    });
    return cells;
}

/** 勾选态 → 规则: 动作集合相同的资源合并成一条, 存库更紧凑、回读更像人写的. */
function cellsToRules(
    cells: Set<string>,
    resources: VocabItem[],
    verbs: VocabItem[],
): Rule[] {
    const bySignature = new Map<string, string[]>();
    resources.forEach((r) => {
        const picked = verbs
            .filter((v) => cells.has(cellKey(r.key, v.key)))
            .map((v) => v.key);
        if (picked.length === 0) return;
        const sig = picked.join(',');
        bySignature.set(sig, [...(bySignature.get(sig) || []), r.key]);
    });
    return Array.from(bySignature.entries()).map(([sig, rs]) => ({
        resources: rs,
        verbs: sig.split(','),
    }));
}

interface Props {
    vocab: Vocabulary;
    /** 编辑既有角色; 不传 = 新建. 内置角色传进来 = 以它为模板另存. */
    source?: RoleInfo;
    onDone: () => void;
    onCancel: () => void;
}

function RoleEditor({ vocab, source, onDone, onCancel }: Props) {
    const isEdit = !!source;
    const [name, setName] = useState(source?.name || '');
    const [displayName, setDisplayName] = useState(source?.displayName || '');
    const [description, setDescription] = useState(source?.description || '');
    const [cells, setCells] = useState<Set<string>>(() =>
        rulesToCells(source?.rules || [], vocab.resources, vocab.verbs),
    );
    const [advanced, setAdvanced] = useState(false);
    // 审批不在角色编辑器里配 (只有系统管理员有, 通配规则天然包含): 矩阵不出这一列;
    // cells 里原有的 approve 保留, 保存时按全量动作回写, 不会把已有审批权抹掉 (2026-09-13).
    const shownVerbs = vocab.verbs.filter(
        (v) => !LEVEL_EXCLUDED_VERBS.includes(v.key),
    );
    // 通配全权角色 (admin: *:*): 档位是展开后的等效视图, 顶部说明一句, 免得被当成手配出来的
    const wildcardAll = (source?.rules || []).some(
        (r) => r.resources.includes('*') && r.verbs.includes('*'),
    );
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');

    const groups = useMemo(() => {
        const map = new Map<string, VocabItem[]>();
        vocab.resources.forEach((r) => {
            const g = r.group || '其他';
            map.set(g, [...(map.get(g) || []), r]);
        });
        return Array.from(map.entries());
    }, [vocab.resources]);

    const applyLevel = (items: VocabItem[], level: Level) => {
        setCells((prev) => {
            const next = new Set(prev);
            items.forEach((r) => {
                // 审批是档位之外的独立开关: 切档不动它 (点"管理员"不该悄悄收走审批权)
                vocab.verbs
                    .filter((v) => !LEVEL_EXCLUDED_VERBS.includes(v.key))
                    .forEach((v) => next.delete(cellKey(r.key, v.key)));
                verbsForLevel(level, r, vocab.verbs).forEach((v) =>
                    next.add(cellKey(r.key, v)),
                );
            });
            return next;
        });
    };

    const toggleCell = (resource: string, verb: string) => {
        setCells((prev) => {
            const next = new Set(prev);
            const k = cellKey(resource, verb);
            if (next.has(k)) next.delete(k);
            else next.add(k);
            return next;
        });
    };

    const stats = useMemo(() => {
        let read = 0;
        let edit = 0;
        const sensitive: string[] = [];
        vocab.resources.forEach((r) => {
            const usable = usableVerbs(r, vocab.verbs);
            const hasRead = usable.some(
                (v) => READ_VERBS.includes(v) && cells.has(cellKey(r.key, v)),
            );
            const hasEdit = usable.some(
                (v) => !READ_VERBS.includes(v) && cells.has(cellKey(r.key, v)),
            );
            if (hasRead) read += 1;
            if (hasEdit) edit += 1;
            if ((hasRead || hasEdit) && r.sensitive) sensitive.push(r.label);
        });
        return { read, edit, sensitive };
    }, [cells, vocab.resources, vocab.verbs]);

    const submit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        const rules = cellsToRules(cells, vocab.resources, vocab.verbs);
        if (rules.length === 0) {
            setError('至少给一个资源域配置权限');
            return;
        }
        setBusy(true);
        try {
            await saveRole({
                name: name.trim(),
                displayName: displayName.trim(),
                description,
                rules,
            });
            onDone();
        } catch (err) {
            setError(
                err instanceof ApiError
                    ? `${err.code}: ${err.message}`
                    : String(err),
            );
        } finally {
            setBusy(false);
        }
    };

    return (
        <form onSubmit={submit} className="space-y-4">
            <div className="flex items-center justify-between">
                <div>
                    <div className="text-sm font-medium text-slate-800">
                        {isEdit ? `编辑角色 ${source.displayName}` : '新建角色'}
                    </div>
                    {source?.system && (
                        <div className="mt-0.5 text-xs text-slate-500">
                            系统角色:按准入身份自动生效,改动会立刻影响所有该身份的人;
                            不可删除,也不能绑到用户组(身份与准入只能对人授予)
                        </div>
                    )}
                </div>
                <div className="flex gap-2">
                    <button
                        type="button"
                        className="btn-ghost"
                        onClick={onCancel}
                    >
                        取消
                    </button>
                    <button
                        type="submit"
                        className="btn-primary"
                        disabled={busy || !name.trim()}
                    >
                        {busy ? '保存中…' : '保存角色'}
                    </button>
                </div>
            </div>

            <div className="card p-4">
                <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                    <label className="block">
                        <span className="mb-1 block text-xs text-slate-500">
                            角色标识
                        </span>
                        <input
                            className="inp font-mono"
                            placeholder="param-viewer"
                            value={name}
                            disabled={isEdit}
                            onChange={(e) => setName(e.target.value)}
                        />
                        <span className="mt-1 block text-xs text-slate-400">
                            小写字母 / 数字 / 连字符,创建后不可改
                        </span>
                    </label>
                    <label className="block">
                        <span className="mb-1 block text-xs text-slate-500">
                            显示名
                        </span>
                        <input
                            className="inp"
                            placeholder="参数只读"
                            value={displayName}
                            onChange={(e) => setDisplayName(e.target.value)}
                        />
                    </label>
                    <label className="block">
                        <span className="mb-1 block text-xs text-slate-500">
                            说明
                        </span>
                        <input
                            className="inp"
                            placeholder="给谁用、为什么这么配"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                        />
                    </label>
                </div>
            </div>

            <div className="card p-4">
                {wildcardAll && (
                    <div className="mb-3 rounded border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-800">
                        该角色为全平台通配授权(
                        <span className="font-mono">*:*</span>
                        ,含审批动作)。下方档位是展开后的等效视图,
                        逐项调整会把通配规则改写成具体资源清单。
                    </div>
                )}
                <div className="mb-3 flex items-center justify-between gap-4">
                    <div className="flex flex-wrap items-center gap-2">
                        <span className="tag bg-sky-50 text-sky-700">
                            只读 {stats.read} 个资源域
                        </span>
                        {stats.edit > 0 && (
                            <span className="tag bg-amber-50 text-amber-700">
                                可管理 {stats.edit} 个
                            </span>
                        )}
                        {stats.sensitive.length > 0 && (
                            <span
                                className="tag bg-rose-50 text-rose-600"
                                title={stats.sensitive.join('、')}
                            >
                                含敏感资源 {stats.sensitive.length} 项
                            </span>
                        )}
                    </div>
                    <button
                        type="button"
                        className="shrink-0 text-xs text-slate-500 hover:text-brand-600"
                        onClick={() => setAdvanced((v) => !v)}
                    >
                        {advanced ? '← 回到档位模式' : '精确配置(按单个动作)→'}
                    </button>
                </div>

                {!advanced && (
                    <div className="space-y-4">
                        {groups.map(([group, items]) => (
                            <div key={group}>
                                <div className="mb-1.5 flex items-center gap-3">
                                    <span className="text-xs font-medium text-slate-500">
                                        {group}
                                    </span>
                                    <span className="text-xs text-slate-300">
                                        |
                                    </span>
                                    {(['none', 'read', 'edit'] as Level[]).map(
                                        (l) => (
                                            <button
                                                key={l}
                                                type="button"
                                                className="text-xs text-slate-400 hover:text-brand-600"
                                                onClick={() =>
                                                    applyLevel(items, l)
                                                }
                                            >
                                                全组{LEVEL_META[l].label}
                                            </button>
                                        ),
                                    )}
                                </div>
                                <div className="divide-y divide-slate-100 rounded-md border border-slate-200">
                                    {items.map((r) => {
                                        const cur = levelOf(
                                            cells,
                                            r,
                                            vocab.verbs,
                                        );
                                        const levels = levelsFor(
                                            r,
                                            vocab.verbs,
                                        );
                                        return (
                                            <div
                                                key={r.key}
                                                className="flex items-center justify-between gap-4 px-3 py-2"
                                            >
                                                <div className="min-w-0">
                                                    <span
                                                        className={`text-sm ${r.sensitive ? 'text-rose-600' : 'text-slate-700'}`}
                                                    >
                                                        {r.label}
                                                    </span>
                                                    {r.sensitive && (
                                                        <span className="tag ml-2 bg-rose-50 text-rose-600">
                                                            敏感
                                                        </span>
                                                    )}
                                                    {r.publicRead && (
                                                        <span
                                                            className="tag ml-2 bg-emerald-50 text-emerald-700"
                                                            title="读接口对所有已准入用户开放, 不需要授权; 这里只配置是否可维护"
                                                        >
                                                            所有人可读
                                                        </span>
                                                    )}
                                                    <span className="ml-2 font-mono text-xs text-slate-400">
                                                        {r.key}
                                                    </span>
                                                    {r.note && (
                                                        <div className="mt-0.5 text-xs text-slate-400">
                                                            {r.note}
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="flex shrink-0 items-center gap-2">
                                                    {cur === null && (
                                                        <span className="tag bg-amber-50 text-amber-700">
                                                            自定义组合
                                                        </span>
                                                    )}
                                                    <div className="flex overflow-hidden rounded-md border border-slate-200">
                                                        {levels.map((l) => (
                                                            <button
                                                                key={l}
                                                                type="button"
                                                                title={
                                                                    l ===
                                                                        'none' &&
                                                                    r.publicRead
                                                                        ? '不可维护 (读接口对所有人开放, 无需授权)'
                                                                        : l ===
                                                                            'read'
                                                                          ? midLevelMeta(
                                                                                r,
                                                                            )
                                                                                .hint
                                                                          : LEVEL_META[
                                                                                l
                                                                            ]
                                                                                .hint
                                                                }
                                                                onClick={() =>
                                                                    applyLevel(
                                                                        [r],
                                                                        l,
                                                                    )
                                                                }
                                                                className={`px-3 py-1 text-xs ${
                                                                    cur === l
                                                                        ? 'bg-brand-600 text-white'
                                                                        : 'bg-white text-slate-500 hover:bg-slate-50'
                                                                }`}
                                                            >
                                                                {l === 'none' &&
                                                                r.publicRead
                                                                    ? '所有人可读'
                                                                    : l ===
                                                                        'read'
                                                                      ? midLevelMeta(
                                                                            r,
                                                                        ).label
                                                                      : LEVEL_META[
                                                                            l
                                                                        ].label}
                                                            </button>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {advanced && (
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr>
                                    <th className="th sticky left-0 bg-white">
                                        资源域
                                    </th>
                                    {shownVerbs.map((v) => (
                                        <th
                                            key={v.key}
                                            className="th text-center"
                                        >
                                            {v.label}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {groups.map(([group, items]) => (
                                    <React.Fragment key={group}>
                                        <tr>
                                            <td
                                                className="td bg-slate-50 text-xs text-slate-500"
                                                colSpan={shownVerbs.length + 1}
                                            >
                                                {group}
                                            </td>
                                        </tr>
                                        {items.map((r) => {
                                            const usable = usableVerbs(
                                                r,
                                                vocab.verbs,
                                            );
                                            return (
                                                <tr key={r.key}>
                                                    <td className="td sticky left-0 bg-white">
                                                        <span
                                                            className={
                                                                r.sensitive
                                                                    ? 'text-rose-600'
                                                                    : 'text-slate-700'
                                                            }
                                                        >
                                                            {r.label}
                                                        </span>
                                                    </td>
                                                    {shownVerbs.map((v) => (
                                                        <td
                                                            key={v.key}
                                                            className="td text-center"
                                                        >
                                                            {usable.includes(
                                                                v.key,
                                                            ) ? (
                                                                <input
                                                                    type="checkbox"
                                                                    checked={cells.has(
                                                                        cellKey(
                                                                            r.key,
                                                                            v.key,
                                                                        ),
                                                                    )}
                                                                    onChange={() =>
                                                                        toggleCell(
                                                                            r.key,
                                                                            v.key,
                                                                        )
                                                                    }
                                                                    aria-label={`${r.label} ${v.label}`}
                                                                />
                                                            ) : (
                                                                // 该资源没有这个动作的路由, 给了也命中不了任何请求
                                                                <span
                                                                    className="text-slate-200"
                                                                    title="该资源无此动作"
                                                                >
                                                                    —
                                                                </span>
                                                            )}
                                                        </td>
                                                    ))}
                                                </tr>
                                            );
                                        })}
                                    </React.Fragment>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {error && (
                <div className="card border-rose-200 bg-rose-50 p-3 text-sm text-rose-600">
                    {error}
                </div>
            )}
        </form>
    );
}

export default RoleEditor;
